/**
 * @file Additives.js
 * @version 1.4.3
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-14
 * @modified 2026-09-29
 * @description Additives Management. Inclusion rate and price are optional and
 *              render as an em-dash when unknown — never as 0, which would claim
 *              the additive is free rather than unpriced.
 */

import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './Additives.css';
import './ContractPricing.css';
import './ResizableColumns.css';
import Input from './Input';
import PaginationControls from './PaginationControls';
import AdditivesModal from './AdditivesModal';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import { formatCurrency } from '../utils/currencyFormatter';
import { useSlideOutButton } from './SlideOutButton';
import useResizableColumns from '../hooks/useResizableColumns';
import useTableContextMenu from '../hooks/useTableContextMenu';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { matchesAllTerms } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';

function Additives() {
  const [additives, setAdditives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedAdditive, setSelectedAdditive] = useState(null);

  // Slideout buttons
  useSlideOutButton();
  // Drag-to-resize columns (7 cols: code, name, inclusion rate, price,
  // supplier, notes, actions).
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns('additives', 7);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: null });
  const [sorting, setSorting] = useState({
    field: '',
    direction: 'asc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    loadAdditives();
  }, []);

  const loadAdditives = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: 10000 // Set high limit for client-side filtering/pagination
      });

      const response = await fetch(`${API_URL}/api/additives?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      const allAdditives = data.data || [];
      setAdditives(allAdditives);

      // Reset to page 1 when data changes
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: allAdditives.length,
        totalPages: Math.ceil(allAdditives.length / prev.limit)
      }));
    } catch (error) {
      console.error('Error loading additives:', error);
      setAdditives([]);
      setPagination(prev => ({ ...prev, page: 1, total: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSorting({ field: '', direction: 'asc' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // The search box is this screen's only filter; it lights Clear Filters.
  const headerFilters = activeHeaderFilters({ search: searchTerm }, { search: '' });
  const filtersApplied = headerFilters.count > 0;

  const handleAddNew = () => {
    setSelectedAdditive(null);
    setShowModal(true);
  };

  const handleEdit = (additive) => {
    setSelectedAdditive(additive);
    setShowModal(true);
  };

  const handleDelete = (additive) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Additive',
      message: `Are you sure you want to delete ${additive.name}?`,
      type: 'error',
      confirmText: 'Delete',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const response = await fetch(`${API_URL}/api/additives/${additive.id}`, {
            method: 'DELETE',
            credentials: 'include'
          });

          if (response.ok) {
            loadAdditives();
          } else {
            setAlertDialog({ isOpen: true, message: 'Failed to delete additive', type: 'error' });
          }
        } catch (error) {
          console.error('Error deleting additive:', error);
          setAlertDialog({ isOpen: true, message: 'Error deleting additive', type: 'error' });
        }
      }
    });
  };

  const handleSave = async () => {
    setShowModal(false);
    loadAdditives();
  };

  // Filter, sort, and paginate additives
  const filteredAndSortedAdditives = additives
    .filter(additive => {
      if (!searchTerm) return true;
      return matchesAllTerms([additive.name, additive.code], searchTerm);
    })
    .sort((a, b) => {
      if (!sorting.field) return 0;

      let aVal = a[sorting.field];
      let bVal = b[sorting.field];

      // Handle numeric fields. These are now nullable, and an unknown price is
      // not a zero one — sort unknowns to the bottom either way rather than
      // filing them among the freebies.
      if (['inclusion_rate', 'price'].includes(sorting.field)) {
        const aNull = aVal === null || aVal === undefined || aVal === '';
        const bNull = bVal === null || bVal === undefined || bVal === '';
        if (aNull || bNull) {
          if (aNull && bNull) return 0;
          return aNull ? 1 : -1;
        }
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        // Handle string fields (case-insensitive)
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sorting.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sorting.direction === 'asc' ? 1 : -1;
      return 0;
    });

  // Update pagination when filters or sorting changes
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      page: 1,
      total: filteredAndSortedAdditives.length,
      totalPages: Math.ceil(filteredAndSortedAdditives.length / prev.limit)
    }));
  }, [filteredAndSortedAdditives.length, searchTerm, sorting.field, sorting.direction]);

  // Paginate the filtered and sorted results
  const paginatedAdditives = filteredAndSortedAdditives.slice(
    (pagination.page - 1) * pagination.limit,
    pagination.page * pagination.limit
  );

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const ctxMenu = useTableContextMenu({
    rows: paginatedAdditives,
    getRowId: (a) => a.id
  });

  const buildRowActions = (additive) => [
    { key: 'edit', icon: 'edit', label: 'Edit', title: 'Edit', onClick: () => handleEdit(additive) },
    { key: 'delete', icon: 'delete', label: 'Delete', title: 'Delete', className: 'action-danger', onClick: () => handleDelete(additive) },
  ];

  return (
    <div className="contract-pricing additive-pricing">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">
            Manage feed additives with inclusion rates and pricing per tonne
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={handleAddNew}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add New</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['additive', 'code']} />
            </label>
            <Input
              type="text"
              placeholder="Additive name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon="search"
            />
          </div>
        </div>
      </div>
      <div className="table-toolbar">
        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
          <ResetButton />
        </div>
        {pagination.totalPages > 0 && (
          <PaginationControls
            pagination={pagination}
            onPageChange={changePage}
          />
        )}
      </div>

      <div className="table-container">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <table className="data-table resizable-table" ref={tableRef} onContextMenu={ctxMenu.onTableContextMenu}>
            <ColGroup />
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('code')}>
                  Code {sorting.field === 'code' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={0} />
                </th>
                <th className="sortable" onClick={() => handleSort('name')}>
                  Name {sorting.field === 'name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={1} />
                </th>
                <th className="text-right sortable" onClick={() => handleSort('inclusion_rate')}>
                  Inc Rate (%) {sorting.field === 'inclusion_rate' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={2} />
                </th>
                <th className="text-right sortable" onClick={() => handleSort('price')}>
                  Price /T {sorting.field === 'price' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={3} />
                </th>
                <th className="sortable" onClick={() => handleSort('supplier_company_name')}>
                  Supplier {sorting.field === 'supplier_company_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={4} />
                </th>
                <th className="sortable" onClick={() => handleSort('notes')}>
                  Notes {sorting.field === 'notes' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={5} />
                </th>
                <th className="actions-col"><ResizeHandle colIndex={6} /></th>
              </tr>
            </thead>
            <tbody>
              {paginatedAdditives.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    No additives found
                  </td>
                </tr>
              ) : (
                paginatedAdditives.map((additive) => (
                  <tr key={additive.id} onContextMenu={ctxMenu.onRowContextMenu(additive)}>
                    <td>{additive.code}</td>
                    <td>{additive.name}</td>
                    {/* Both are optional now. An em-dash says "we weren't told";
                        rendering 0.00% or £0.00 would claim we were. */}
                    <td className="text-right">
                      {additive.inclusion_rate === null || additive.inclusion_rate === undefined
                        ? <span style={{ color: 'var(--text-muted)' }} title="Not supplied">—</span>
                        : `${parseFloat(additive.inclusion_rate).toFixed(2)}%`}
                    </td>
                    <td className="text-right">
                      {additive.price === null || additive.price === undefined
                        ? <span style={{ color: 'var(--text-muted)' }} title="Not supplied">—</span>
                        : formatCurrency(additive.price)}
                    </td>
                    <td>{additive.supplier_company_name || <em style={{ color: 'var(--text-muted)' }}>Any</em>}</td>
                    <td>{additive.notes || '-'}</td>
                    <td className="actions-col block">
                      <RowActionsMenu actions={buildRowActions(additive)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <AdditivesModal
          additive={selectedAdditive}
          onClose={() => { setShowModal(false); loadAdditives(); }}
          onSave={handleSave}
        />
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        onConfirm={() => { if (confirmDialog.onConfirm) confirmDialog.onConfirm(); }}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          { key: 'add', icon: 'add', label: 'Add new', onClick: handleAddNew },
          { key: 'refresh', icon: 'refresh', label: 'Refresh', onClick: loadAdditives },
          { key: 'clear-filters', icon: 'filter_alt_off', label: 'Clear filters', onClick: handleClearFilters }
        ]}
      />
    </div>
  );
}

export default Additives;
